import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import type { Observable } from 'rxjs';
import { defer } from 'rxjs';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { distinctUntilChanged, filter, mergeMap, take } from 'rxjs/operators';

import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkDrag, CdkDragHandle, CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';

import type { CrmDeal } from '@integrations/crm-deals';
import { injectCrmDealsQuery } from '@integrations/crm-deals';
import { type CrmContact, injectCrmContactsQuery, isCrmDisabledError } from '@integrations/crm-contacts';
import type { CrmPipeline } from '@integrations/crm-pipelines';
import { injectCrmPipelinesQuery } from '@integrations/crm-pipelines';
import type { CrmTag } from '@integrations/crm-tags';
import { injectCrmTagsQuery } from '@integrations/crm-tags';
import type { AddressBookVariable } from '@integrations/address-books';
import { AddressBooksUiComponent, injectAddressBooksQuery } from '@integrations/address-books';
import type { PaymentMethodOption } from '@integrations/payment-methods';
import { injectPaymentMethodsOptionsQuery, injectPaymentMethodsQuery } from '@integrations/payment-methods';
import type { WebLegacyWidgetEntity } from '@web/types';
import type { CourseTariffSelection } from '@web/widgets/common/course-tariff-selector/course-tariff-selector.component';
import { CourseTariffSelectorComponent } from '@web/widgets/common/course-tariff-selector/course-tariff-selector.component';
import type { EduCourse, EduCourseWithTariffs } from '@common/types/edu-course.type';

import type { FormWidgetElement, PaymentElementLegacy, WidgetPositionLegacy } from '@sites/entity-core';
import type { FormControlType } from '@common/types/form-element.type';
import type {
    PaymentFormWidgetDataModel,
    PaymentFormWidgetDefaultForm,
    PaymentFormWidgetForm,
    PaymentFormWidgetZeroForm,
} from '@sites/entity-signal-form';

import { EFormInputType, EHtmlAutocompleteAttr, EHtmlInputType } from '@common/enums';
import { EPixelTypes } from '@common/types/analytics-setings.type';
import { EPaymentView } from '@common/enums/payment-form-view.enum';
import { ERadioGroupType, SpRadioGroupComponent } from '@ui/sp-radio-group';
import { HtmlToTextPipe } from '@common/pipes/html-to-text.pipe';
import { SpControlHandleComponent } from '@ui/sp-control-handle';
import { SpSwitchComponent } from '@ui/sp-switch';
import { SpVariablesPopoverComponent } from '@ui/sp-variables-popover';

import { AppFacade } from '@web/store/app/app.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { EduCoursesCollectionFacade } from '@web/store/collections/edu-courses/edu-courses.facade';
import { EduCoursePaymentsCollectionFacade } from '@web/store/collections/edu-course-payments/edu-course-payments.facade';

import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';

import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

import { WebAnalyticsSettingsSignalLegacyComponent } from '@web/shared/components/web-analytics-settings-signal-legacy/web-analytics-settings-signal-legacy.component';
import {
    type SetPipelineEventPayload,
    WebCrmPipelinesCollectionComponent,
} from '@web/shared/components/web-crm-pipelines-collection';
import { WebTagsCollectionComponent } from '@web/shared/components/web-tags-collection/web-tags-collection.component';
import { WebWidgetExtendedHideSettingsComponent } from '@web/shared/components/web-widget-extended-hide-settings';
import { WebWidgetHideSettingsComponent } from '@web/shared/components/web-widget-hide-settings';
import { WebWidgetPositionSettingsComponent } from '@web/shared/components/web-widget-position-settings';
import { SpExtendedPositionSignalSettingsComponent } from '@components/sp-extended-position-signal-settings';
import { WebEditorModule } from '@web/shared/components/web-editor/web-editor.module';
import { WebEduCoursePaymentsCollectionComponent } from '@web/shared/components/web-edu-course-payments-collection/web-edu-course-payments-collection.component';
import { WebPaymentMethodsSignalCollectionComponent } from '@web/shared/components/web-payment-methods-collection/web-payment-methods-signal-collection.component';
import { createDefaultPaymentElementLegacy } from '@web/shared/components/web-payment-methods-collection/web-payment-methods-collection.helpers';
import { WebSynchronizeStyleToggleComponent } from '@web/shared/components/web-synchronize-style-toggle';
import { WebCustomIdComponent } from '@web/shared/components/web-custom-id/web-custom-id.component';
import { WebDeviceToggleComponent } from '@web/shared/components/web-device-toggle/web-device-toggle.component';

import { PaidAccessNotificationContainerComponent } from '@web/shared/components/web-settings/paid-access-notification/paid-access-notification-container.component';
import { WebSettingsModule } from '@web/shared/components/web-settings/web-settings.module';

import type { PaymentFormWidgetData } from '@web/widgets/payment-form/type/payment-form-widget.type';
import { ProductType } from '@web/widgets/payment-form/type/payment-form-widget.type';
import {
    defaultPaymentWidgetEduElements,
    defaultPaymentWidgetElements,
} from '@web/widgets/payment-form/constants/default-payment-form-element.conts';
import { PaymentAdditionalSettingsComponent } from './payment-additional-settings/payment-additional-settings.component';
import { PaymentButtonSettingsComponent } from './payment-button-settings/payment-button-settings.component';
import { PaymentElementSettingsComponent } from './payment-element-settings/payment-element-settings.component';
import { PaymentInputSettingsComponent } from './payment-input-settings/payment-input-settings.component';
import { PaymentMethodSettingsComponent } from './payment-method-settings/payment-method-settings.component';
import { FormField } from '@angular/forms/signals';
import { type AddressBookTag, injectAddressBooksTagsQuery } from '@integrations/address-books-tags';

enum CurrentSettings {
    main = 'main',
    button = 'button',
    element = 'element',
    input = 'input',
    additional = 'additional',
    payment = 'payment',
    analytics = 'analytics',
}

/** Mirrors `Validators.required` semantics for plain values (0 is non-empty). */
/** Snapshot of everything a product-type switch overwrites — see `productTypeConfigs`. */
type ProductTypeConfig = {
    elements: FormWidgetElement[];
    paymentElements: PaymentElementLegacy[];
    productName: PaymentFormWidgetDataModel['productName'];
    productSettings: PaymentFormWidgetDataModel['productSettings'];
    selectedPipeline: PaymentFormWidgetDataModel['selectedPipeline'];
    selectedPipelineStep: PaymentFormWidgetDataModel['selectedPipelineStep'];
    selectedPipelineSettings: PaymentFormWidgetDataModel['selectedPipelineSettings'];
    createAddressBook: boolean;
};

/**
 * Signal-form rewrite of the payment-form control panel. The widget data lives
 * in the shared `PaymentFormWidgetForm` (see `WebLegacyWidgetSignalFormService`); every
 * sub-panel edits it live inside a transaction frame it opens and closes itself
 * (`FormSettingsPanel`), so save commits one history entry and cancel/escape/closing
 * the panel rolls the edits back. The CRM pipelines / tags /
 * address-books and payment-method collections all speak the signal
 * value-in/event-out API — no bridge controls remain (the payment-method
 * settings panel keeps a local FormGroup only because its lib is still
 * FormGroup-bound).
 */
@Component({
    selector: 'web-payment-form-control',
    templateUrl: './payment-form-control.component.html',
    styleUrls: ['./payment-form-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TranslateModule,
        TooltipModule,
        CdkDropList,
        CdkDrag,
        CdkDropListGroup,
        CdkDragHandle,
        CommonElementsModule,
        WebSettingsModule,
        PaidAccessNotificationContainerComponent,
        SpSwitchComponent,
        SpControlHandleComponent,
        SpRadioGroupComponent,
        SpVariablesPopoverComponent,
        AddressBooksUiComponent,
        WebCrmPipelinesCollectionComponent,
        WebTagsCollectionComponent,
        WebEduCoursePaymentsCollectionComponent,
        WebPaymentMethodsSignalCollectionComponent,
        WebDeviceToggleComponent,
        WebSynchronizeStyleToggleComponent,
        WebCustomIdComponent,
        WebEditorModule,
        CourseTariffSelectorComponent,
        WebWidgetPositionSettingsComponent,
        WebWidgetHideSettingsComponent,
        WebWidgetExtendedHideSettingsComponent,
        SpExtendedPositionSignalSettingsComponent,
        WebAnalyticsSettingsSignalLegacyComponent,
        HtmlToTextPipe,
        PaymentButtonSettingsComponent,
        PaymentElementSettingsComponent,
        PaymentInputSettingsComponent,
        PaymentAdditionalSettingsComponent,
        PaymentMethodSettingsComponent,
        FormField,
    ],
})
export class PaymentFormControlComponent implements OnInit {
    public readonly widget = input.required<WebLegacyWidgetEntity<PaymentFormWidgetData>>();
    public readonly isSpecific = input<boolean>(false);
    public readonly isEduSite = input<boolean>(false);

    private readonly translateService: TranslateService = inject(TranslateService);
    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly appFacade: AppFacade = inject(AppFacade);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly eduCoursesCollectionFacade: EduCoursesCollectionFacade = inject(EduCoursesCollectionFacade);
    private readonly eduCoursePaymentsCollectionFacade: EduCoursePaymentsCollectionFacade = inject(
        EduCoursePaymentsCollectionFacade
    );
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    // --- Collections / queries ---

    private readonly isAdmin = toSignal(this.appFacade.isAdmin$, { initialValue: false });

    protected readonly paymentMethodsQuery = injectPaymentMethodsQuery();
    protected readonly paymentMethodsOptionsQuery = injectPaymentMethodsOptionsQuery();
    public readonly paymentMethods = computed(() => (this.isAdmin() ? [] : (this.paymentMethodsQuery.data() ?? [])));
    public readonly paymentMethodsOptions = computed(() => this.paymentMethodsOptionsQuery.data() ?? []);

    protected readonly crmPipelinesQuery = injectCrmPipelinesQuery(() => ({ enabled: !this.isAdmin() }));
    public readonly crmPipelines = computed<CrmPipeline[]>(() =>
        this.isAdmin() ? [] : (this.crmPipelinesQuery.data() ?? [])
    );
    private readonly crmPipelines$: Observable<CrmPipeline[]> = toObservable(this.crmPipelines);

    protected readonly crmContactsQuery = injectCrmContactsQuery(() => ({ enabled: !this.isAdmin() }));
    public readonly crmContacts = computed<CrmContact[]>(() =>
        this.isAdmin() ? [] : (this.crmContactsQuery.data() ?? [])
    );
    /**
     * Only a definitive rejection of contacts/attributes means CRM is disabled for the
     * account — a transient network failure leaves the CRM settings in place (#71534).
     * Admin/template mode has no real account (the query is disabled), so it never
     * counts as CRM-disabled regardless of the query state.
     */
    public readonly isCrmDisabled = computed(
        () => !this.isAdmin() && isCrmDisabledError(this.crmContactsQuery.error())
    );

    protected readonly crmTagsQuery = injectCrmTagsQuery(() => ({ enabled: !this.isAdmin() }));
    public readonly crmTags = computed<CrmTag[]>(() => (this.isAdmin() ? [] : (this.crmTagsQuery.data() ?? [])));
    protected readonly addressBooksTagsQuery = injectAddressBooksTagsQuery(() => ({ enabled: !this.isAdmin() }));
    public readonly addressBooksTags = computed<AddressBookTag[]>(() =>
        this.isAdmin() ? [] : (this.addressBooksTagsQuery.data() ?? [])
    );

    /** The deals query key follows the pipeline selected in the widget data. */
    private readonly selectedPipelineId = computed<number | null>(() => {
        const pipeline = (this.form?.selectedPipeline().value() ?? null) as CrmPipeline | null;
        return pipeline?.id ?? null;
    });
    protected readonly crmDealsQuery = injectCrmDealsQuery(
        () => this.selectedPipelineId(),
        () => ({ enabled: !this.isAdmin() })
    );
    public readonly crmDeals = computed<CrmDeal[]>(() => (this.isAdmin() ? [] : (this.crmDealsQuery.data() ?? [])));

    public readonly eduCourses$: Observable<EduCourseWithTariffs[]> = this.appFacade.isAdminCollectionWrapper$(
        this.eduCoursesCollectionFacade.eduCoursesSortByPaid$
    );
    public readonly isEduCoursesEmpty$: Observable<boolean> = this.eduCoursesCollectionFacade.isEduCoursesEmpty$;

    // --- Signal form ---

    public form!: PaymentFormWidgetForm;

    private readonly isAddressBookEnabled = computed(() => Boolean(this.form?.createAddressBook().value()));
    protected readonly addressBooksQuery = injectAddressBooksQuery(() => ({ enabled: this.isAddressBookEnabled() }));

    public addressBookVariablesById(bookId: number | null): AddressBookVariable[] {
        return this.addressBooksQuery.data()?.find((b) => b.id === bookId)?.variables ?? [];
    }

    /** The first payment element's type drives the success-page option. */
    public readonly isHasSuccessPage = computed<boolean>(() => {
        const type = this.form?.paymentElements().value()?.[0]?.type ?? null;
        return (
            this.paymentMethodsOptions().find((item: PaymentMethodOption) => item.paymentMethodType === type)
                ?.isBackUrlSuccess ?? false
        );
    });

    // Dynamic edu-course payment streams — re-resolved when the picked course changes.
    private readonly courseId$ = toObservable(
        computed(() => this.form?.productSettings().value()?.courseId ?? null)
    ).pipe(
        filter((courseId): courseId is number => Boolean(courseId)),
        distinctUntilChanged()
    );

    public readonly eduCoursePaymentsDynamic$ = defer(() =>
        this.courseId$.pipe(
            mergeMap((courseId) =>
                this.appFacade.isAdminCollectionWrapper$(
                    this.eduCoursePaymentsCollectionFacade.eduCoursePayments$(courseId)
                )
            )
        )
    );

    public readonly eduCoursePaymentsStatusesDynamic$ = defer(() =>
        this.courseId$.pipe(
            mergeMap((courseId) => this.eduCoursePaymentsCollectionFacade.eduCoursePaymentsStatuses$(courseId))
        )
    );

    // --- Panel state ---

    public readonly settingEnum = CurrentSettings;
    public readonly currentSettings = signal<CurrentSettings>(CurrentSettings.main);
    public readonly editingInputIndex = signal<number | null>(null);

    public readonly isDeviceDesktop = toSignal(this.layoutFacade.isDeviceDesktop$, { initialValue: true });
    public readonly isDeviceTablet = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });
    public readonly isDeviceMobile = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });

    public readonly radioGroupType = ERadioGroupType;
    public readonly productTypeEnum = ProductType;
    protected readonly EPixelTypes = EPixelTypes;
    protected readonly EFormInputType = EFormInputType;
    protected readonly EPaymentView = EPaymentView;

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<PaymentFormWidgetForm>(this.widget().id, this.destroyRef);

        // Edu course: backfill product name / paid flag once courses arrive.
        this.eduCourses$
            .pipe(
                filter((courses: EduCourseWithTariffs[]) => Boolean(courses?.length)),
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((courses: EduCourseWithTariffs[]) => {
                const settings = this.form.productSettings().value();
                if (!settings?.courseId) return;

                const course = courses.find((c) => c.id === settings.courseId);

                this.form.transactWithoutHistory(() => {
                    if (settings.courseId && !settings.courseTariffId) {
                        this.form.setProductSettings({
                            courseId: course?.id || null,
                            courseTariffId: settings.courseTariffId,
                            isPaid: settings.courseTariffId ? true : course?.hasPaidTariffs,
                        } as PaymentFormWidgetDataModel['productSettings']);
                    }
                    if (
                        (!settings.courseId && !settings.courseTariffId) ||
                        (settings.courseId && settings.courseTariffId)
                    ) {
                        this.form.setProductSettings({
                            courseId: course?.id || null,
                            courseTariffId: settings.courseTariffId ?? undefined,
                            isPaid: !!settings.courseTariffId,
                        } as PaymentFormWidgetDataModel['productSettings']);
                    }

                    this.form.setProductName(course?.name || null);
                });
            });
    }

    // --- Validation / widget errors ---

    // --- Panel switching (each sub-panel opens and closes its own transaction) ---

    public setCurrentSettingsType(panel: CurrentSettings): void {
        this.currentSettings.set(panel);
    }

    public closeSettings(): void {
        this.currentSettings.set(CurrentSettings.main);
        this.editingInputIndex.set(null);
    }

    // --- CRM pipelines / tags / address book ---

    public setPipeline({ pipeline, step, settings, skipHistory }: SetPipelineEventPayload): void {
        const apply = () => {
            this.form.setSelectedPipeline(pipeline as unknown as PaymentFormWidgetDataModel['selectedPipeline']);
            this.form.setSelectedPipelineStep(step as unknown as PaymentFormWidgetDataModel['selectedPipelineStep']);
            this.form.setSelectedPipelineSettings(
                settings as unknown as PaymentFormWidgetDataModel['selectedPipelineSettings']
            );
            this.clearCrmVariables();
        };

        if (skipHistory) {
            this.form.transactWithoutHistory(apply);
            return;
        }

        this.form.transact(apply);
    }

    private clearCrmVariables(): void {
        const elements = (this.form.elements().value() ?? []).map((el) => ({
            ...el,
            crmContactVariable: null,
            crmDealVariable: null,
        }));

        this.form.setElements(elements);
    }

    private clearAddressBookVariables(): void {
        const elements = (this.form.elements().value() ?? []).map((el) => ({
            ...el,
            variable: el.type === EFormInputType.email ? el.variable : null,
        }));

        this.form.setElements(elements);
    }

    public onSelectedAddressBookChange(bookId: number): void {
        this.form.transact(() => {
            this.form.setSelectedAddressBook(bookId);
            this.clearAddressBookVariables();
        });
    }

    public toggleAddressBookSettings(val: boolean): void {
        this.form.transact(() => {
            if (!val) {
                this.clearAddressBookVariables();
            }

            this.form.setSelectedAddressBook(null);
            this.form.setCreateAddressBook(val);
        });
    }

    public toggleAddCrmTags(val: boolean): void {
        this.form.transact(() => {
            if (!val) {
                this.form.setContactTags([]);
            }

            this.form.setAddCrmTags(val);
        });
    }

    public toggleAddAddressBookTags(val: boolean): void {
        this.form.transact(() => {
            if (!val) {
                this.form.setContactAddressBookTags([]);
            }

            this.form.setAddAddressBookTags(val);
        });
    }

    public toggleModalTextHint(val: boolean): void {
        this.form.setModalTextHint(val ? '' : null);
    }

    // --- Payment elements (array, value-in/event-out) ---

    /**
     * The collections emit `setSkipHistoryEvent` immediately before the
     * matching `setPaymentElementsEvent` for writes that must not land in undo
     * history (default-element seeding, edu-course payment load). We latch the
     * flag and consume it on the next `setPaymentElements`.
     */
    private skipNextPaymentElementsHistory = false;

    public setSkipHistory(): void {
        this.skipNextPaymentElementsHistory = true;
    }

    public setPaymentElements(paymentElements: PaymentElementLegacy[]): void {
        if (this.skipNextPaymentElementsHistory) {
            this.skipNextPaymentElementsHistory = false;
            this.form.transactWithoutHistory(() => this.form.setPaymentElements(paymentElements));
            return;
        }

        this.form.setPaymentElements(paymentElements);
    }

    public editPaymentElement(index: number): void {
        this.editingInputIndex.set(index);
        this.setCurrentSettingsType(CurrentSettings.payment);
    }

    // --- Elements (form fields) ---

    public editInput(index: number): void {
        this.editingInputIndex.set(index);
        this.setCurrentSettingsType(CurrentSettings.element);
    }

    public removeInput(i: number): void {
        this.form.removeElement(i);
    }

    public addInput(): void {
        this.form.addElement({
            type: EFormInputType.input,
            label: this.translateService.instant('control_entry_field'),
            variable: null,
            crmContactVariable: null,
            crmDealVariable: null,
            placeholder: this.translateService.instant('control_input_placeholder'),
            required: false,
            protected: false,
            htmlInputType: EHtmlInputType.text,
            autocompleteType: EHtmlAutocompleteAttr.off,
        } as unknown as FormWidgetElement);
    }

    public dropElementItem(event: CdkDragDrop<FormWidgetElement[]>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        this.form.moveElement(event.previousIndex, event.currentIndex);
    }

    public getControlHandleIcon(type: string): string {
        return `./assets/img/websites/forms-icons/lpc-f-${type}-icon.svg`;
    }

    // --- Product type / edu course ---

    /**
     * Per-product-type snapshots of the fields a switch replaces wholesale, so a
     * there-and-back switch is lossless within the panel session. Without it the
     * branches below reset the payment provider + price, the form fields and the CRM
     * wiring to defaults — and the re-seeded default payment element carries no
     * amount, which makes the published form reject its own submit
     * (`422 {"errors":{"totalCost":["The total cost field is required."]}}`).
     */
    private readonly productTypeConfigs = new Map<ProductType, ProductTypeConfig>();

    public changeProductType(event: Event): void {
        const productType = (event.target as HTMLInputElement).value as ProductType;
        const previousType = this.form.productType().value() as ProductType;

        if (productType === previousType) {
            return;
        }

        this.productTypeConfigs.set(previousType, this.readProductTypeConfig());
        const restored = this.productTypeConfigs.get(productType);

        this.form.transact(() => {
            this.form.setProductType(productType as PaymentFormWidgetDataModel['productType']);

            if (restored) {
                this.writeProductTypeConfig(restored);

                // A snapshot taken before the methods query resolved can be element-less;
                // seed here rather than leaving it to the collection (see below).
                if (productType === ProductType.default && !restored.paymentElements.length) {
                    this.seedDefaultPaymentElement();
                }

                return;
            }

            if (productType === ProductType.default) {
                this.form.setProductName(this.translateService.instant('control_product_name'));
                this.form.setElements(structuredClone(defaultPaymentWidgetElements) as unknown as FormWidgetElement[]);
                this.form.setProductSettings(null);
                this.seedDefaultPaymentElement();

                this.crmPipelines$
                    .pipe(
                        filter((pipelines: CrmPipeline[]) => Boolean(pipelines?.length)),
                        take(1),
                        takeUntilDestroyed(this.destroyRef)
                    )
                    .subscribe((pipelines: CrmPipeline[]) => {
                        const pipeline: CrmPipeline = structuredClone(pipelines[0]);
                        this.form.transactWithoutHistory(() => {
                            this.form.setSelectedPipeline(
                                pipeline as unknown as PaymentFormWidgetDataModel['selectedPipeline']
                            );
                            this.form.setSelectedPipelineStep(
                                pipeline.steps[0] as unknown as PaymentFormWidgetDataModel['selectedPipelineStep']
                            );
                            this.form.setSelectedPipelineSettings(
                                pipeline.settings as unknown as PaymentFormWidgetDataModel['selectedPipelineSettings']
                            );
                        });
                    });
                return;
            }

            // Edu course branch — the payment elements come from the course's own
            // payments, seeded by `web-edu-course-payments-collection` once they load.
            this.form.setPaymentElements([]);
            this.form.setElements(
                defaultPaymentWidgetEduElements(this.translateService) as unknown as FormWidgetElement[]
            );
            this.form.setCreateAddressBook(false);
            this.form.setSelectedPipeline(null);
            this.form.setSelectedPipelineStep(null);
            this.form.setSelectedPipelineSettings(null);

            this.eduCourses$
                .pipe(take(1), takeUntilDestroyed(this.destroyRef))
                .subscribe((courses: EduCourseWithTariffs[]) => {
                    const paidCourses = courses.filter((c) => c.tariffs.some((t) => t.isPaid));

                    this.form.transact(() => {
                        if (paidCourses.length) {
                            const paidTariffs = paidCourses[0].tariffs.filter((tariff) => tariff.isPaid);
                            this.form.setProductSettings({
                                courseId: paidCourses[0].id,
                                courseTariffId: paidTariffs[0]?.id,
                                isPaid: paidTariffs[0]?.isPaid,
                            } as PaymentFormWidgetDataModel['productSettings']);
                            this.form.setProductName(paidCourses[0].name);
                        } else {
                            this.form.setProductSettings(null);
                        }
                    });
                });
        });
    }

    /**
     * Seeds the provider element as part of the switch instead of leaving an empty list
     * for `web-payment-methods-signal-collection`'s seeding effect. That effect writes
     * *after* the switch has been dispatched to the store, and the entity→model patch
     * that echoes the dispatch back reverts the write — the list then stays empty until
     * the next builder reload. With no methods loaded (admin/template mode, where the
     * query is disabled) leave the seeding to the collection, as before.
     */
    private seedDefaultPaymentElement(): void {
        const paymentMethods = this.paymentMethods();

        if (!paymentMethods.length) {
            this.form.setPaymentElements([]);
            return;
        }

        this.form.setPaymentElements([
            createDefaultPaymentElementLegacy(paymentMethods, this.translateService.instant('custom_payment_label')),
        ]);
    }

    private readProductTypeConfig(): ProductTypeConfig {
        return structuredClone({
            elements: this.form.elements().value() ?? [],
            paymentElements: this.form.paymentElements().value() ?? [],
            productName: this.form.productName().value() ?? null,
            productSettings: this.form.productSettings().value() ?? null,
            selectedPipeline: this.form.selectedPipeline().value() ?? null,
            selectedPipelineStep: this.form.selectedPipelineStep().value() ?? null,
            selectedPipelineSettings: this.form.selectedPipelineSettings().value() ?? null,
            createAddressBook: Boolean(this.form.createAddressBook().value()),
        }) as ProductTypeConfig;
    }

    private writeProductTypeConfig(config: ProductTypeConfig): void {
        // Clone on the way out too: the snapshot stays reusable if the user switches
        // back and forth more than once.
        const value = structuredClone(config);

        this.form.setElements(value.elements);
        this.form.setPaymentElements(value.paymentElements);
        this.form.setProductName(value.productName);
        this.form.setProductSettings(value.productSettings);
        this.form.setSelectedPipeline(value.selectedPipeline);
        this.form.setSelectedPipelineStep(value.selectedPipelineStep);
        this.form.setSelectedPipelineSettings(value.selectedPipelineSettings);
        this.form.setCreateAddressBook(value.createAddressBook);
    }

    public setEduCourseToProduct(selection: CourseTariffSelection): void {
        this.eduCourses$
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe((courses: EduCourseWithTariffs[]) => {
                const course = courses.find((item: EduCourse) => item.id === Number(selection.course.id));

                this.form.transact(() => {
                    this.form.setProductName(course?.name ?? null);
                    this.form.setProductSettings({
                        courseId: Number(selection.course.id),
                        courseTariffId: selection?.tariffId,
                        isPaid: selection?.tariffId ? true : course?.hasPaidTariffs,
                    } as PaymentFormWidgetDataModel['productSettings']);
                });
            });
    }

    // --- View / sync style ---

    public setInputSettingsHorizontalPosition(val: string): void {
        const settings = this.form.inputSettings().value();
        if (!settings) return;

        this.form.setInputSettings({
            ...settings,
            horizontalPosition: val as PaymentFormWidgetDataModel['inputSettings']['horizontalPosition'],
        });
    }

    // --- Derived flags ---

    public readonly isProductTypeDefault = computed(() => this.form?.productType().value() === ProductType.default);

    public readonly isProductTypeEduCourse = computed(() => this.form?.productType().value() === ProductType.eduCourse);

    public get filteredFormControls(): FormControlType[] {
        const result: FormControlType[] = [EFormInputType.file];

        if (this.isProductTypeDefault()) {
            return result;
        }

        result.push(EFormInputType.date);

        const hasPhone = (this.form?.elements().value() ?? []).some((el) => el.type === EFormInputType.phone);
        if (hasPhone) {
            result.push(EFormInputType.phone);
        }

        return result;
    }

    public readonly isFormControlDisabled = computed(() => {
        if (this.isProductTypeDefault()) {
            return false;
        }

        const index = this.editingInputIndex();
        return Boolean(this.form?.elements().value()?.[index]?.protected);
    });

    public readonly isAddressBookListDisabled = computed(() => {
        if (this.isProductTypeEduCourse()) {
            return true;
        }

        return !this.form?.createAddressBook().value();
    });

    public readonly isCrmDealsListDisabled = computed(() => this.isProductTypeEduCourse());

    public readonly isCrmContactListDisabled = computed(() => {
        if (this.isProductTypeDefault()) {
            return false;
        }

        const index = this.editingInputIndex();
        return Boolean(this.form?.elements().value()?.[index]?.protected);
    });

    // --- Layout / position ---

    /** True when the widget lives inside a zero block. */
    public readonly isZero = computed(() => this.widget().type === 'zero');

    /** The form narrowed to the default variant — see the form control for the casting rationale. */
    public get defaultForm(): PaymentFormWidgetDefaultForm {
        return this.form as PaymentFormWidgetDefaultForm;
    }

    /** The form narrowed to the zero variant. */
    public get zeroForm(): PaymentFormWidgetZeroForm {
        return this.form as PaymentFormWidgetZeroForm;
    }

    public onPositionChange(patch: Partial<WidgetPositionLegacy>): void {
        const current = this.defaultForm.position().value();
        this.defaultForm.setPosition({ ...current, ...patch });
    }
}
